import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  EmptyStateModule,
  ModalComponent,
  ModalModule,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig,
  TableModule
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { map, finalize } from 'rxjs';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { debounceTime, distinctUntilChanged, take } from 'rxjs/operators';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedModule } from '../../../../shared/shared.module';
import { CasingCreditDetails } from '../../models/casing-credit-details.model';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';

@Component({
  selector: 'app-casing-credit-copy',
  templateUrl: './casing-credit-copy.component.html',
  styleUrl: './casing-credit-copy.component.scss',
  imports: [SharedModule, TableModule, EmptyStateModule, ModalModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CasingCreditCopyComponent implements OnInit, CanComponentDeactivate {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<CasingCreditDetails>;
  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public router = inject(Router);
  public alertService = inject(AlertService);
  public route = inject(ActivatedRoute);
  public cdr = inject(ChangeDetectorRef);

  casingCreditId: string;
  codeForm: FormGroup;
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  casingCreditDetails: CasingCreditDetails[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  initialFormValues: any = {}; // Store initial form values for change detection
  allModifiedRows: Map<string, any> = new Map(); // Store all modified rows for future reference
  pagination: any;
  pageSizeOptions = [10, 25, 50, 100];
  currentPage = 1;
  pageSize = 10;
  productSaveDisabled = true;
  isLoading = false;

  sizeList: string[] = [];
  descriptionList: string[] = [];
  baseCreditList: string[] = [];
  repairGridList: string[] = [];

  tableConfig: TableConfig<any> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    columns: {}
  };

  fetcher: PaginatorFetcher<any> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    // Track existing modifications before fetching new data
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (formArray && this.casingCreditDetails.length > 0) {
      // Loop through the current displayed rows to store any modifications
      for (let i = 0; i < this.casingCreditDetails.length; i++) {
        if (i < formArray.length) {
          const currentRow = this.casingCreditDetails[i];
          const currentFormValue = formArray.at(i).value;
          // Only store if values are different from initial or if already in the map
          if (this.isRowModified(currentRow, currentFormValue) || this.allModifiedRows.has(currentRow.casingCreditDetailId)) {
            this.allModifiedRows.set(currentRow.casingCreditDetailId, currentFormValue);
          }
        }
      }
    }

    // Use the values from the codeForm when fetching data
    return this.productDataService
      .getCasingCreditDetails({ ...this.filterFormBackend.value, ...filters }, this.casingCreditId, filters.page, filters.limit)
      .pipe(
        map((response: any) => {
          this.casingCreditDetails = response.data.casingCreditDetails || [];
          this.pagination = response.data.pagination;
          this.updateDisplayedRowsForm(this.casingCreditDetails);
          return { items: this.casingCreditDetails, count: this.pagination.totalRecords };
        })
      );
  };

  // Helper method to check if a row has been modified
  private isRowModified(originalRow: CasingCreditDetails, formValue: any): boolean {
    return originalRow.baseCredit !== formValue.baseCredit || originalRow.repairGrid !== formValue.repairGrid;
  }

  constructor() {
    this.codeForm = this.fb.group({
      code: ['', Validators.required],
      description: ['', Validators.required]
    });
    this.filterForm = this.fb.group({
      size: [[]],
      description: [[]],
      baseCredit: [[]],
      repairGrid: [[]]
    });
    this.filterFormBackend = this.fb.group({
      size: [[]],
      description: [[]],
      baseCredit: [[]],
      repairGrid: [[]]
    });
  }

  ngOnInit(): void {
    // Initialize the allModifiedRows Map
    this.allModifiedRows = new Map<string, any>();

    // Store initial form values for change detection
    this.initialFormValues = {
      code: this.codeForm.value.code,
      description: this.codeForm.value.description,
      rows: []
    };

    this.route.params.subscribe((params) => {
      this.casingCreditId = params['casingCreditId'];

      // Load data only after we have the ID
      if (this.casingCreditId) {
        this.loadFiltersAndData();
      }
    });

    // Set up table columns based on translations
    this.configureTableColumns();

    // Set up form change listeners
    this.setupFormListeners();
  }

  private loadFiltersAndData(): void {
    // Check if dual write is enabled (affects save button state)
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);

    // Load filter dropdown options
    this.productDataService.getCasingCreditDetailsSearchFilters(this.casingCreditId).subscribe((response: any) => {
      this.sizeList = response.data.sizes || [];
      this.descriptionList = response.data.descriptions || [];
      this.baseCreditList = response.data.baseCredits || [];
      this.repairGridList = response.data.repairGrids || [];

      this.generateFilterForm();
    });
  }

  private setupFormListeners(): void {
    // Monitor filter form changes and update the table
    this.filterForm.valueChanges.pipe(distinctUntilChanged(), debounceTime(800)).subscribe(() => {
      // Generate backend filter values and apply to table
      this.generateFilterForm();
      if (this.tableRef) {
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    });

    // Removed the codeForm valueChanges subscription to prevent API calls when typing
    // If you want to manually trigger data refresh, add a button in the template and call this.tableRef.resetFilters()
  }

  private configureTableColumns(): void {
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        casingSize: { cell: (row) => row.size, header: this.translationService.translate('casingSizeText') },
        description: { cell: (row) => row.description, header: this.translationService.translate('descriptionText') },
        baseCredit: { cell: 'baseCredit', header: this.translationService.translate('baseCreditText') },
        repairGrid: {
          cell: 'repairGridDropdown',
          header: this.translationService.translate('repairGridText')
        }
      };
      this.cdr.markForCheck();
    });
  }

  createRowForm(row: CasingCreditDetails): FormGroup {
    // Check if we have a modified version of this row
    const modifiedRow = this.allModifiedRows.get(row.casingCreditDetailId);

    return this.fb.group({
      casingCreditDetailId: [row.casingCreditDetailId],
      size: [row.size],
      description: [row.description],
      baseCredit: [modifiedRow ? modifiedRow.baseCredit : row.baseCredit],
      repairGrid: [modifiedRow ? modifiedRow.repairGrid : row.repairGrid],
      casingSizeFk: [row.casingSizeFk],
      casingCreditRepairFk: [row.casingCreditRepairFk]
    });
  }

  updateDisplayedRowsForm(rows: any[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);

    // Store initial form values to track changes
    this.initialFormValues = {
      code: this.codeForm.value.code,
      description: this.codeForm.value.description,
      rows: JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue().rows))
    };

    // Set up a single valueChanges subscription for the displayed rows form
    this.displayedRowsForm.valueChanges.subscribe(() => {
      // Update the allModifiedRows Map with the current form values
      const formArray = this.displayedRowsForm.get('rows') as FormArray;
      for (let i = 0; i < this.casingCreditDetails.length; i++) {
        if (i < formArray.length) {
          const row = this.casingCreditDetails[i];
          const formValue = formArray.at(i).value;

          // Check if the row has been modified
          if (this.isRowModified(row, formValue)) {
            this.allModifiedRows.set(row.casingCreditDetailId, formValue);
          } else if (this.allModifiedRows.has(row.casingCreditDetailId)) {
            // If it's no longer modified, remove it from the map
            this.allModifiedRows.delete(row.casingCreditDetailId);
          }
        }
      }

      // Update save button state when form values change
      this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
      this.cdr.markForCheck();
    });
  }

  getControl(item: CasingCreditDetails, controlName: string): FormControl {
    const index = this.casingCreditDetails.findIndex((row) => row.casingCreditDetailId === item.casingCreditDetailId);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  clearFilters(): void {
    this.filterForm.reset();
  }

  /**
   * Helper to open the unsaved changes modal and return a Promise<boolean> for the user's choice
   */
  private openUnsavedChangesModal(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.unsavedChangesModal.open({
        id: 'unsaved-confirmation',
        width: 500,
        title: this.translationService.translate('discardChangesText'),
        content: this.unsavedChangesContentModal,
        confirm: this.translationService.translate('okText'),
        cancel: this.translationService.translate('cancelLinkText')
      });
      this.unsavedChangesModal.result.pipe(take(1)).subscribe((result: ModalResult) => {
        resolve(result && result.result === true);
      });
    });
  }

  cancel(): void {
    // Modal will be opened by canDeactivate if there are unsaved changes
    this.router
      .navigateByUrl('/home/productData/casingCredit')
      .then(() => {})
      .catch((error) => {
        console.error('Navigation failed:', error);
      });
  }

  /**
   * Checks if there are any unsaved changes in the form
   */
  isFormChanged(): boolean {
    if (!this.initialFormValues) {
      return false;
    }

    // Check if code or description changed
    if (
      this.codeForm.value.code !== this.initialFormValues.code ||
      this.codeForm.value.description !== this.initialFormValues.description
    ) {
      return true;
    }

    // Check if we have any modified rows
    return this.allModifiedRows.size > 0;
  }

  /**
   * Implementation of CanComponentDeactivate interface
   * Uses the shared utility to handle the modal confirmation
   */
  async canDeactivate(): Promise<boolean> {
    if (!this.isFormChanged()) {
      return true;
    }
    return this.openUnsavedChangesModal();
  }

  /**
   * Gets modified rows for saving
   * Combines current form modifications with allModifiedRows from previous pages
   */
  getModifiedRows(): any[] {
    const modifiedRows: any[] = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    // Collect modified rows from the current form
    if (formArray && this.casingCreditDetails.length > 0) {
      for (let i = 0; i < this.casingCreditDetails.length; i++) {
        const row = this.casingCreditDetails[i];
        const formValue = formArray.at(i).value;
        // Only add if modified
        if (this.isRowModified(row, formValue)) {
          modifiedRows.push({
            casingCreditDetailId: row.casingCreditDetailId,
            size: formValue.size,
            description: formValue.description,
            baseCredit: formValue.baseCredit,
            repairGrid: formValue.repairGrid,
            casingSizeFk: row.casingSizeFk,
            casingCreditRepairFk: row.casingCreditRepairFk
          });
        }
      }
    }
    // Add any previously modified rows not currently displayed
    this.allModifiedRows.forEach((value, key) => {
      if (!modifiedRows.some((row) => row.casingCreditDetailId === key)) {
        modifiedRows.push(value);
      }
    });
    return modifiedRows;
  }

  save(): void {
    // Validate the form first
    if (this.codeForm.invalid) {
      // Mark form fields as touched to trigger validation messages
      this.codeForm.markAllAsTouched();
      // Show alert for required fields
      this.alertService.showAlert({
        type: AlertType.classical,
        content: this.translationService.translate('missingValuesText')
      } as Alert);
      return;
    }

    // Show loading indicator
    this.isLoading = true;
    this.productSaveDisabled = true;
    this.cdr.markForCheck();

    const code = this.codeForm.get('code').value;
    const description = this.codeForm.get('description').value;

    const modifiedRows = this.getModifiedRows();
    if (modifiedRows.length > 0) {
      this.allModifiedRows.forEach((item, key) => {
        if (!modifiedRows.some((row) => row.casingCreditDetailId === key)) {
          modifiedRows.push(item);
        }
      });
      const payload = {
        casingCreditDetails: modifiedRows
      };

      this.productDataService
        .copyCasingCreate(code, description, payload, this.casingCreditId)
        .pipe(
          finalize(() => {
            this.isLoading = false;
            this.productSaveDisabled = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: () => {
            // Update the initial form values after successful save
            this.initialFormValues = {
              code: this.codeForm.value.code,
              description: this.codeForm.value.description,
              rows: JSON.parse(JSON.stringify(this.displayedRowsForm.getRawValue().rows))
            };
            // Clear the modified rows map
            this.allModifiedRows.clear();

            // Mark form as pristine
            this.codeForm.markAsPristine();

            this.alertService.showAlert({
              type: AlertType.success,
              content: this.translationService.translate('casingCreditCreatedSuccessfully')
            } as Alert);

            this.router.navigateByUrl('/home/productData/casingCredit');
          },
          error: (error) => {
            console.error('Error creating casing credit copy:', error);
            this.alertService.showAlert({
              type: AlertType.critical,
              content: error?.error?.message || this.translationService.translate('errorOccurred')
            } as Alert);
          }
        });
    } else {
      this.isLoading = false;
      this.productSaveDisabled = false;
      this.alertService.showAlert({
        type: AlertType.classical,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
      this.cdr.markForCheck();
    }
  }

  generateFilterForm(): void {
    const filterSize = this.filterForm.get('size').value;
    const filterDescription = this.filterForm.get('description').value;
    const filterBaseCredit = this.filterForm.get('baseCredit').value;
    const filterRepairGrid = this.filterForm.get('repairGrid').value;

    this.filterFormBackend.patchValue({
      size: filterSize || '',
      description: filterDescription || '',
      baseCredit: filterBaseCredit || '',
      repairGrid: filterRepairGrid || ''
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.isFormChanged()) {
      $event.returnValue = true;
    }
  }

  checkIfCasingCreditCodeExists() {
    const casingCreditCode = this.codeForm.get('code').value;
    this.productDataService.casingCreditCodeExists(casingCreditCode).subscribe((exists) => {
      if (exists) {
        this.codeForm.get('code').setErrors({ exists: true });
      } else {
        this.codeForm.get('code').setErrors(null);
      }
    });
  }
}
